/**
 * Notification Settings Component
 * 
 * Allows users to manage their notification preferences
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Bell, 
  Mail, 
  Smartphone, 
  MessageSquare, 
  Clock, 
  Globe,
  ToggleLeft,
  ToggleRight,
  Save,
  RotateCcw
} from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { UserNotificationPreferences } from '../../@types/notification';
import { ThemeColors, Spacing, Typography, Shadows } from '../../styles/ThemeStyles';

const SettingsContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: ${Shadows.lg};
  overflow: hidden;
`;

const Header = styled.div`
  padding: ${Spacing.xl};
  border-bottom: 1px solid ${ThemeColors.border.light};
  background: ${ThemeColors.primary.main};
  color: white;
`;

const Title = styled.h2`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const Content = styled.div`
  padding: ${Spacing.xl};
`;

const Section = styled.div`
  margin-bottom: ${Spacing.xxl};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  margin-bottom: ${Spacing.lg};
`;

const SectionIcon = styled.div<{ color?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.color || ThemeColors.primary.light};
  color: ${props => props.color ? 'white' : ThemeColors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SectionTitle = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0;
`;

const SectionDescription = styled.p`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  margin: ${Spacing.xs} 0 0 0;
`;

const SettingItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.lg} 0;
  border-bottom: 1px solid ${ThemeColors.border.light};
  
  &:last-child {
    border-bottom: none;
  }
`;

const SettingInfo = styled.div`
  flex: 1;
`;

const SettingLabel = styled.label`
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
  cursor: pointer;
  display: block;
  margin-bottom: ${Spacing.xs};
`;

const SettingDescription = styled.div`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  margin: 0;
  line-height: 1.4;
`;

const ToggleButton = styled.button<{ enabled: boolean }>`
  width: 50px;
  height: 28px;
  border-radius: 14px;
  border: none;
  background: ${props => props.enabled ? ThemeColors.primary.main : ThemeColors.border.medium};
  cursor: pointer;
  position: relative;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: ${props => props.enabled ? ThemeColors.primary.dark : ThemeColors.border.dark};
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.enabled ? '24px' : '2px'};
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: white;
    transition: left 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const Select = styled.select`
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: 6px;
  background: white;
  color: ${ThemeColors.text.primary};
  font-size: ${Typography.fontSize.sm};
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light};
  }
`;

const TimeInput = styled.input`
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: 6px;
  background: white;
  color: ${ThemeColors.text.primary};
  font-size: ${Typography.fontSize.sm};
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light};
  }
`;

const QuietHoursContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  margin-top: ${Spacing.md};
`;

const QuietHoursLabel = styled.span`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
`;

const Actions = styled.div`
  padding: ${Spacing.xl};
  border-top: 1px solid ${ThemeColors.border.light};
  display: flex;
  gap: ${Spacing.md};
  justify-content: flex-end;
`;

const Button = styled.button<{ variant?: string }>`
  padding: ${Spacing.md} ${Spacing.lg};
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: 6px;
  background: white;
  color: ${ThemeColors.text.primary};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${ThemeColors.background.light};
  }
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: ${ThemeColors.primary.main};
          color: white;
          border-color: ${ThemeColors.primary.main};
          
          &:hover {
            background: ${ThemeColors.primary.dark};
          }
        `;
      default:
        return '';
    }
  }}
`;

const PermissionButton = styled.button`
  padding: ${Spacing.xs} ${Spacing.sm};
  border: 1px solid ${ThemeColors.primary.main};
  border-radius: 6px;
  background: ${ThemeColors.primary.main};
  color: white;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: ${ThemeColors.primary.dark};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PermissionStatus = styled.div<{ status: string }>`
  font-size: ${Typography.fontSize.xs};
  color: ${props => {
    switch (props.status) {
      case 'granted': return ThemeColors.success.main;
      case 'denied': return ThemeColors.error.main;
      default: return ThemeColors.text.secondary;
    }
  }};
  margin-top: ${Spacing.xs};
`;

const SuccessMessage = styled.div`
  padding: ${Spacing.md};
  background: ${ThemeColors.success.light};
  color: ${ThemeColors.success.main};
  border-radius: 6px;
  margin-bottom: ${Spacing.md};
  font-size: ${Typography.fontSize.sm};
`;

const ErrorMessage = styled.div`
  padding: ${Spacing.md};
  background: ${ThemeColors.error.light};
  color: ${ThemeColors.error.main};
  border-radius: 6px;
  margin-bottom: ${Spacing.md};
  font-size: ${Typography.fontSize.sm};
`;

const LoadingState = styled.div`
  padding: ${Spacing.xxl};
  text-align: center;
  color: ${ThemeColors.text.secondary};
`;

interface NotificationSettingsProps {
  onClose: () => void;
}

export const NotificationSettings: React.FC<NotificationSettingsProps> = ({ onClose }) => {
  const { preferences, updatePreferences, isLoading } = useNotifications();
  const {
    isSupported,
    permission,
    isEnabled: pushEnabled,
    fcmToken,
    isLoading: pushLoading,
    error: pushError,
    enablePushNotifications,
    disablePushNotifications,
    testNotification
  } = usePushNotifications();
  const [localPreferences, setLocalPreferences] = useState<UserNotificationPreferences | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (preferences) {
      setLocalPreferences(preferences);
    }
  }, [preferences]);

  const handleToggle = async (section: keyof UserNotificationPreferences, field: string, value: boolean) => {
    if (!localPreferences) return;

    // Special handling for push notifications
    if (section === 'push' && field === 'enabled') {
      try {
        if (value) {
          // Enable push notifications
          await enablePushNotifications();
          setMessage({ type: 'success', text: 'Push notifications enabled successfully!' });
        } else {
          // Disable push notifications
          await disablePushNotifications();
          setMessage({ type: 'success', text: 'Push notifications disabled' });
        }
      } catch (error: any) {
        setMessage({ type: 'error', text: error.message || 'Failed to update push notifications' });
        return; // Don't update local state if API call failed
      }
    }

    setLocalPreferences(prev => {
      if (!prev) return prev;
      
      const newPrefs = { ...prev };
      if (section === 'global') {
        (newPrefs[section] as any)[field] = value;
      } else {
        (newPrefs[section] as any)[field] = value;
      }
      return newPrefs;
    });
    setHasChanges(true);
  };

  const handleSelectChange = (section: keyof UserNotificationPreferences, field: string, value: string) => {
    if (!localPreferences) return;

    setLocalPreferences(prev => {
      if (!prev) return prev;
      
      const newPrefs = { ...prev };
      if (section === 'global') {
        (newPrefs[section] as any)[field] = value;
      } else {
        (newPrefs[section] as any)[field] = value;
      }
      return newPrefs;
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!localPreferences) return;

    try {
      await updatePreferences(localPreferences);
      setHasChanges(false);
      onClose();
    } catch (error) {
      console.error('Failed to save preferences:', error);
    }
  };

  const handleReset = () => {
    if (preferences) {
      setLocalPreferences(preferences);
      setHasChanges(false);
    }
  };

  if (isLoading || !localPreferences) {
    return (
      <SettingsContainer>
        <LoadingState>Loading notification settings...</LoadingState>
      </SettingsContainer>
    );
  }

  return (
    <SettingsContainer>
      <Header>
        <Title>
          <Bell size={24} />
          Notification Settings
        </Title>
      </Header>

      <Content>
        {/* Email Notifications */}
        <Section>
          <SectionHeader>
            <SectionIcon color="#3B82F6">
              <Mail size={20} />
            </SectionIcon>
            <div>
              <SectionTitle>Email Notifications</SectionTitle>
              <SectionDescription>
                Configure how and when you receive email notifications
              </SectionDescription>
            </div>
          </SectionHeader>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Enable Email Notifications</SettingLabel>
              <SettingDescription>
                Receive notifications via email
              </SettingDescription>
            </SettingInfo>
            <ToggleButton
              enabled={localPreferences.email.enabled}
              onClick={() => handleToggle('email', 'enabled', !localPreferences.email.enabled)}
            />
          </SettingItem>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Order Updates</SettingLabel>
              <SettingDescription>
                Get notified when orders are created, updated, or completed
              </SettingDescription>
            </SettingInfo>
            <ToggleButton
              enabled={localPreferences.email.orderUpdates}
              onClick={() => handleToggle('email', 'orderUpdates', !localPreferences.email.orderUpdates)}
            />
          </SettingItem>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>System Alerts</SettingLabel>
              <SettingDescription>
                Important system notifications and maintenance alerts
              </SettingDescription>
            </SettingInfo>
            <ToggleButton
              enabled={localPreferences.email.systemAlerts}
              onClick={() => handleToggle('email', 'systemAlerts', !localPreferences.email.systemAlerts)}
            />
          </SettingItem>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Weekly Reports</SettingLabel>
              <SettingDescription>
                Receive weekly performance and activity reports
              </SettingDescription>
            </SettingInfo>
            <ToggleButton
              enabled={localPreferences.email.weeklyReports}
              onClick={() => handleToggle('email', 'weeklyReports', !localPreferences.email.weeklyReports)}
            />
          </SettingItem>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Security Alerts</SettingLabel>
              <SettingDescription>
                Critical security notifications and login alerts
              </SettingDescription>
            </SettingInfo>
            <ToggleButton
              enabled={localPreferences.email.securityAlerts}
              onClick={() => handleToggle('email', 'securityAlerts', !localPreferences.email.securityAlerts)}
            />
          </SettingItem>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Digest Frequency</SettingLabel>
              <SettingDescription>
                How often to receive digest emails
              </SettingDescription>
            </SettingInfo>
            <Select
              value={localPreferences.email.digestFrequency}
              onChange={(e) => handleSelectChange('email', 'digestFrequency', e.target.value)}
            >
              <option value="never">Never</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </Select>
          </SettingItem>
        </Section>

        {/* Push Notifications */}
        <Section>
          <SectionHeader>
            <SectionIcon color="#10B981">
              <Smartphone size={20} />
            </SectionIcon>
            <div>
              <SectionTitle>Push Notifications</SectionTitle>
              <SectionDescription>
                Real-time notifications delivered to your device
              </SectionDescription>
            </div>
          </SectionHeader>

          {message && (
            message.type === 'success' ? (
              <SuccessMessage>{message.text}</SuccessMessage>
            ) : (
              <ErrorMessage>{message.text}</ErrorMessage>
            )
          )}

          {pushError && (
            <ErrorMessage>{pushError}</ErrorMessage>
          )}

          {!isSupported && (
            <ErrorMessage>
              Push notifications are not supported in this browser. Please use a modern browser like Chrome, Firefox, or Edge.
            </ErrorMessage>
          )}

          {permission === 'denied' && (
            <ErrorMessage>
              Notification permission was denied. Please enable notifications in your browser settings to receive push notifications.
            </ErrorMessage>
          )}

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Enable Push Notifications</SettingLabel>
              <SettingDescription>
                Receive real-time push notifications on your device
                {fcmToken && (
                  <div style={{ marginTop: Spacing.xs, fontSize: Typography.fontSize.xs }}>
                    Token registered: {fcmToken.substring(0, 20)}...
                  </div>
                )}
                {permission && (
                  <PermissionStatus status={permission}>
                    Permission: {permission === 'granted' ? '✓ Granted' : permission === 'denied' ? '✗ Denied' : '? Not set'}
                  </PermissionStatus>
                )}
              </SettingDescription>
            </SettingInfo>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: Spacing.xs }}>
              <ToggleButton
                enabled={pushEnabled && permission === 'granted'}
                onClick={() => handleToggle('push', 'enabled', !pushEnabled)}
                disabled={pushLoading || !isSupported || permission === 'denied'}
              />
              {permission !== 'granted' && permission !== 'denied' && (
                <PermissionButton
                  onClick={async () => {
                    try {
                      await enablePushNotifications();
                      setMessage({ type: 'success', text: 'Permission granted! Push notifications enabled.' });
                    } catch (error: any) {
                      setMessage({ type: 'error', text: error.message || 'Failed to request permission' });
                    }
                  }}
                  disabled={pushLoading || !isSupported}
                >
                  Request Permission
                </PermissionButton>
              )}
              {permission === 'granted' && pushEnabled && (
                <PermissionButton
                  onClick={async () => {
                    try {
                      await testNotification();
                      setMessage({ type: 'success', text: 'Test notification sent!' });
                    } catch (error: any) {
                      setMessage({ type: 'error', text: error.message || 'Failed to send test notification' });
                    }
                  }}
                  disabled={pushLoading}
                  style={{ background: ThemeColors.secondary.main }}
                >
                  Send Test
                </PermissionButton>
              )}
            </div>
          </SettingItem>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Order Updates</SettingLabel>
              <SettingDescription>
                Instant notifications for order status changes
              </SettingDescription>
            </SettingInfo>
            <ToggleButton
              enabled={localPreferences.push.orderUpdates}
              onClick={() => handleToggle('push', 'orderUpdates', !localPreferences.push.orderUpdates)}
              disabled={!pushEnabled}
            />
          </SettingItem>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>System Alerts</SettingLabel>
              <SettingDescription>
                Important system notifications
              </SettingDescription>
            </SettingInfo>
            <ToggleButton
              enabled={localPreferences.push.systemAlerts}
              onClick={() => handleToggle('push', 'systemAlerts', !localPreferences.push.systemAlerts)}
              disabled={!pushEnabled}
            />
          </SettingItem>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Reminders</SettingLabel>
              <SettingDescription>
                Task reminders and deadline notifications
              </SettingDescription>
            </SettingInfo>
            <ToggleButton
              enabled={localPreferences.push.reminders}
              onClick={() => handleToggle('push', 'reminders', !localPreferences.push.reminders)}
              disabled={!pushEnabled}
            />
          </SettingItem>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Quiet Hours</SettingLabel>
              <SettingDescription>
                Disable push notifications during specified hours
              </SettingDescription>
            </SettingInfo>
            <ToggleButton
              enabled={localPreferences.push.quietHours.enabled}
              onClick={() => {
                const newQuietHours = { 
                  ...localPreferences.push.quietHours, 
                  enabled: !localPreferences.push.quietHours.enabled 
                };
                setLocalPreferences(prev => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    push: {
                      ...prev.push,
                      quietHours: newQuietHours
                    }
                  };
                });
                setHasChanges(true);
              }}
              disabled={!pushEnabled}
            />
          </SettingItem>

          {localPreferences.push.quietHours.enabled && (
            <QuietHoursContainer>
              <QuietHoursLabel>From:</QuietHoursLabel>
              <TimeInput
                type="time"
                value={localPreferences.push.quietHours.startTime}
                onChange={(e) => {
                  setLocalPreferences(prev => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      push: {
                        ...prev.push,
                        quietHours: {
                          ...prev.push.quietHours,
                          startTime: e.target.value
                        }
                      }
                    };
                  });
                  setHasChanges(true);
                }}
                disabled={!pushEnabled}
              />
              <QuietHoursLabel>To:</QuietHoursLabel>
              <TimeInput
                type="time"
                value={localPreferences.push.quietHours.endTime}
                onChange={(e) => {
                  setLocalPreferences(prev => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      push: {
                        ...prev.push,
                        quietHours: {
                          ...prev.push.quietHours,
                          endTime: e.target.value
                        }
                      }
                    };
                  });
                  setHasChanges(true);
                }}
                disabled={!pushEnabled}
              />
            </QuietHoursContainer>
          )}
        </Section>

        {/* Global Settings */}
        <Section>
          <SectionHeader>
            <SectionIcon color="#6B7280">
              <Globe size={20} />
            </SectionIcon>
            <div>
              <SectionTitle>Global Settings</SectionTitle>
              <SectionDescription>
                General notification preferences
              </SectionDescription>
            </div>
          </SectionHeader>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Language</SettingLabel>
              <SettingDescription>
                Language for notifications
              </SettingDescription>
            </SettingInfo>
            <Select
              value={localPreferences.global.language}
              onChange={(e) => handleSelectChange('global', 'language', e.target.value)}
            >
              <option value="en">English</option>
              <option value="fr">Français</option>
            </Select>
          </SettingItem>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Timezone</SettingLabel>
              <SettingDescription>
                Timezone for notification timing
              </SettingDescription>
            </SettingInfo>
            <Select
              value={localPreferences.global.timezone}
              onChange={(e) => handleSelectChange('global', 'timezone', e.target.value)}
            >
              <option value="America/Toronto">Eastern Time</option>
              <option value="America/New_York">Eastern Time (US)</option>
              <option value="America/Chicago">Central Time</option>
              <option value="America/Denver">Mountain Time</option>
              <option value="America/Los_Angeles">Pacific Time</option>
            </Select>
          </SettingItem>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Digest Time</SettingLabel>
              <SettingDescription>
                Preferred time for daily digest emails
              </SettingDescription>
            </SettingInfo>
            <TimeInput
              type="time"
              value={localPreferences.global.digestTime}
              onChange={(e) => handleSelectChange('global', 'digestTime', e.target.value)}
            />
          </SettingItem>
        </Section>
      </Content>

      <Actions>
        <Button onClick={handleReset} disabled={!hasChanges}>
          <RotateCcw size={16} />
          Reset
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!hasChanges}>
          <Save size={16} />
          Save Changes
        </Button>
      </Actions>
    </SettingsContainer>
  );
};
